import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Facebook, Instagram } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-900 text-gray-300" itemScope itemType="https://schema.org/LocalBusiness">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Company Info - NAP Format */}
          <div>
            <Link to="/" className="flex items-center mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_solar-epc-oman/artifacts/746jwpae_ECOSUN%20%281%29.svg" 
                alt="Ecosun Renewables Logo - Solar EPC Company in Oman" 
                className="h-16 w-16 rounded-full"
                itemProp="logo"
                width="64"
                height="64"
                loading="lazy"
                decoding="async"
              />
            </Link>
            <p itemProp="name" className="font-semibold text-white mb-2">Ecosun Renewables</p>
            <p className="text-sm mb-4" itemProp="description">
              Leading Solar EPC company in Oman, specializing in grid-connected solar power systems, battery energy storage, and comprehensive solar O&M services.
            </p>
            <div className="flex space-x-3">
              <a href="https://www.linkedin.com/company/ecosunrenewables" target="_blank" rel="noopener noreferrer" aria-label="Follow Ecosun Renewables on LinkedIn" className="w-9 h-9 rounded-full bg-gray-800 hover:bg-emerald-600 flex items-center justify-center transition-colors duration-200">
                <Linkedin className="w-4 h-4" />
              </a>
              <a href="https://www.facebook.com/ecosunrenewables" target="_blank" rel="noopener noreferrer" aria-label="Follow Ecosun Renewables on Facebook" className="w-9 h-9 rounded-full bg-gray-800 hover:bg-emerald-600 flex items-center justify-center transition-colors duration-200">
                <Facebook className="w-4 h-4" />
              </a>
              <a href="https://www.instagram.com/ecosunrenewables" target="_blank" rel="noopener noreferrer" aria-label="Follow Ecosun Renewables on Instagram" className="w-9 h-9 rounded-full bg-gray-800 hover:bg-emerald-600 flex items-center justify-center transition-colors duration-200">
                <Instagram className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/" className="hover:text-emerald-400 transition-colors">Home</Link></li>
              <li><Link to="/services" className="hover:text-emerald-400 transition-colors">Solar Services</Link></li>
              <li><Link to="/blog" className="hover:text-emerald-400 transition-colors">Blog & News</Link></li>
              <li><Link to="/calculator" className="hover:text-emerald-400 transition-colors">Solar Calculator</Link></li>
              <li><Link to="/contact" className="hover:text-emerald-400 transition-colors">Contact Us</Link></li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-white font-semibold mb-4">Our Solar Services</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/services" className="hover:text-emerald-400 transition-colors">Grid Connected Solar Systems</Link></li>
              <li><Link to="/services" className="hover:text-emerald-400 transition-colors">Battery Storage Systems (BESS)</Link></li>
              <li><Link to="/services" className="hover:text-emerald-400 transition-colors">Solar Consultancy Services</Link></li>
              <li><Link to="/services" className="hover:text-emerald-400 transition-colors">Solar O&M Services</Link></li>
            </ul>
          </div>

          {/* Contact Info - Structured NAP */}
          <div itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
            <h3 className="text-white font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start space-x-2">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-400" />
                <span>
                  <span itemProp="addressLocality">Muscat</span>, <span itemProp="addressCountry">Sultanate of Oman</span>
                </span>
              </li>
              <li className="flex items-center space-x-2">
                <Phone className="w-4 h-4 flex-shrink-0 text-emerald-400" />
                <a href="tel:+96891305426" itemProp="telephone" className="hover:text-emerald-400 transition-colors">+968 91305426</a>
              </li>
              <li className="flex items-center space-x-2">
                <Mail className="w-4 h-4 flex-shrink-0 text-emerald-400" />
                <a href="mailto:sales@ecosunrenewables.com" itemProp="email" className="hover:text-emerald-400 transition-colors">sales@ecosunrenewables.com</a>
              </li>
            </ul>
            <meta itemProp="url" content="https://ecosunrenewables.com" />
          </div>
        </div>

        {/* Service Areas */}
        <div className="pt-6 pb-8 border-t border-gray-800">
          <p className="text-sm text-gray-400 text-center">
            <strong className="text-gray-300">Service Areas:</strong> Muscat • Sohar • Salalah • Nizwa • Sur • Ibri • Barka • Rustaq • All of Oman
          </p>
        </div>

        {/* Bottom Bar */}
        <div className="pt-6 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center text-sm">
            <p>&copy; {currentYear} Ecosun Renewables. All rights reserved. | Solar EPC Company in Oman</p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <Link to="/privacy-policy" className="hover:text-emerald-400 transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-emerald-400 transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
